import * as Dfns from 'date-fns';
import * as R from 'ramda';
import { Dispatch, ReactElement, SetStateAction, useCallback, useMemo, useState } from 'react';
import { Toast } from 'react-bootstrap';
import { MdClose, MdInfoOutline } from 'react-icons/md';
import {
  InventoryForecastField,
  InventoryHistoricalField
} from '@blisspointmedia/bpm-types/dist/CommerceOps';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { SparkChart } from '@Components/SparkChart/SparkChart';
import { Size } from '@Components/designConstants';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { VOLUME_FORMATTER } from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { DATE_FORMAT } from '@src/utils/data';
import { dateToMddyy } from '@src/utils/date-utils';
import { CommerceOpsTable } from './CommerceOpsTable';
import {
  constructMetricFormatter,
  DimensionConfig,
  INVENTORY_FORECAST_DESCRIPTIVE_FIELD_TO_FORMATTER,
  INVENTORY_FORECAST_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
  INVENTORY_HISTORICAL_DESCRIPTIVE_FIELD_TO_FORMATTER,
  INVENTORY_HISTORICAL_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
  INVENTORY_METRIC_TO_FORMATTER,
  INVENTORY_METRIC_TO_PRETTY_NAME,
  INVENTORY_TOPLINE_METADATA_MAP,
  InventoryDimension,
  InventoryForecastDescriptiveField,
  InventoryHistoricalDescriptiveField,
  InventoryTabProps,
  onKpiToggle
} from './CommerceOpsUtils';

const kpiFormatter = (
  row: {
    kpi: InventoryHistoricalField | InventoryForecastField;
    [key: string]: number | string | InventoryHistoricalField | InventoryForecastField;
  },
  field: string
): string => INVENTORY_METRIC_TO_FORMATTER[row.kpi](row[field] as number);

const GLOBAL_KPI_TOGGLES = [
  {
    field: InventoryForecastField.ON_HAND_INVENTORY,
    label: INVENTORY_METRIC_TO_PRETTY_NAME[InventoryForecastField.ON_HAND_INVENTORY]
  },
  {
    field: InventoryForecastField.TOTAL_UNITS_SOLD,
    label: INVENTORY_METRIC_TO_PRETTY_NAME[InventoryForecastField.TOTAL_UNITS_SOLD]
  }
];

const HISTORICAL_KPI_TOGGLES = [
  {
    field: InventoryHistoricalField.PAID_SALES,
    label: INVENTORY_METRIC_TO_PRETTY_NAME[InventoryHistoricalField.PAID_SALES]
  },
  {
    field: InventoryHistoricalField.AD_SPEND,
    label: INVENTORY_METRIC_TO_PRETTY_NAME[InventoryHistoricalField.AD_SPEND]
  },
  {
    field: InventoryHistoricalField.ROAS,
    label: INVENTORY_METRIC_TO_PRETTY_NAME[InventoryHistoricalField.ROAS]
  }
];

const XLSX_MENU_OPTIONS = ['XLSX'];

const INVENTORY_DIMENSIONS = Object.values(InventoryDimension);

const Inventory = ({
  data: { primaryTimeSeries, compTimeSeries, forecast, forecastMetricsKeys, historical },
  primaryDateRange,
  downloadTable,
  config
}: InventoryTabProps): ReactElement => {
  const [masterFilterObj, setMasterFilterObj] = useState<{
    filterFn: (line: Record<string, any>) => boolean;
  }>();

  const masterDataMap = useMemo(
    () => ({
      [InventoryDimension.FORECAST]: forecast,
      [InventoryDimension.HISTORICAL]: historical
    }),
    [forecast, historical]
  );

  const [hiddenGlobalKpis, setHiddenGlobalKpis] = useState<Set<string>>(new Set());

  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastLabel, setToastLabel] = useState<string>('');
  const onToastClose = () => {
    setToastLabel('');
    setShowToast(false);
  };

  const onHistoricalKpiToggle = useCallback(
    (
      kpi: { field: string; label: string },
      hiddenKpis: Set<string>,
      setHiddenKpis: Dispatch<SetStateAction<Set<string>>>
    ): void =>
      onKpiToggle({
        kpi,
        tableLabel: 'Historical Metrics table',
        hiddenKpis,
        setHiddenKpis,
        setShowToast,
        setToastLabel
      }),
    [setShowToast, setToastLabel]
  );

  const forecastMetricsEnum = useMemo(
    () => Object.assign({}, ...forecastMetricsKeys.map(date => ({ [date]: date }))),
    [forecastMetricsKeys]
  );

  const forecastMetricsToPrettyName = useMemo(
    () =>
      Object.assign(
        {},
        ...forecastMetricsKeys.map(date => ({
          [date]: dateToMddyy(date)
        }))
      ),
    [forecastMetricsKeys]
  );

  const { historicalMetricsEnum, historicalMetricsToPrettyName } = useMemo(() => {
    const historicalMetricsKeys = Dfns.eachWeekOfInterval(
      { start: Dfns.parseISO(primaryDateRange.start), end: Dfns.parseISO(primaryDateRange.end) },
      { weekStartsOn: 1 }
    ).map(date => Dfns.format(date, DATE_FORMAT));
    const metricsEnum = Object.assign({}, ...historicalMetricsKeys.map(date => ({ [date]: date })));

    const metricsToPrettyName = Object.assign(
      {},
      ...historicalMetricsKeys.map(date => ({
        [date]: dateToMddyy(date)
      }))
    );

    return {
      historicalMetricsEnum: metricsEnum,
      historicalMetricsToPrettyName: metricsToPrettyName
    };
  }, [primaryDateRange]);

  const forecastDescriptiveFields: InventoryForecastDescriptiveField[] = useMemo(() => {
    const fieldArr = [
      InventoryForecastDescriptiveField.ASIN,
      InventoryForecastDescriptiveField.PRODUCT_NAME
    ];

    if (!hiddenGlobalKpis.has(InventoryForecastField.ON_HAND_INVENTORY)) {
      fieldArr.push(
        InventoryForecastDescriptiveField.ON_HAND_WEEKS_OF_COVER,
        InventoryForecastDescriptiveField.ON_HAND_STATUS
      );
    }
    fieldArr.push(InventoryForecastDescriptiveField.KPI);
    return fieldArr;
  }, [hiddenGlobalKpis]);

  const inventoryDimensionConfigMap: {
    [InventoryDimension.FORECAST]: DimensionConfig<InventoryForecastDescriptiveField>;
    [InventoryDimension.HISTORICAL]: DimensionConfig<InventoryHistoricalDescriptiveField>;
  } = useMemo(
    () => ({
      [InventoryDimension.FORECAST]: {
        dimensionPrettyName: '12-week Forecast',
        dimensionSuffix: `| w/o ${dateToMddyy(forecastMetricsKeys[0])} - w/o ${dateToMddyy(
          forecastMetricsKeys[forecastMetricsKeys.length - 1]
        )}`,
        descriptiveFields: forecastDescriptiveFields,
        descriptiveFieldToPrettyName: INVENTORY_FORECAST_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
        descriptiveFieldToFormatter: INVENTORY_FORECAST_DESCRIPTIVE_FIELD_TO_FORMATTER,
        descriptiveFieldWidth: {
          [InventoryForecastDescriptiveField.ASIN]: 150,
          [InventoryForecastDescriptiveField.PRODUCT_NAME]: 200,
          [InventoryForecastDescriptiveField.ON_HAND_WEEKS_OF_COVER]: 200,
          [InventoryForecastDescriptiveField.ON_HAND_STATUS]: 150,
          [InventoryForecastDescriptiveField.KPI]: 200
        },
        metricsEnum: forecastMetricsEnum,
        metricToPrettyName: forecastMetricsToPrettyName,
        rowGroupingField: InventoryForecastDescriptiveField.ASIN,
        rowFormatter: kpiFormatter,
        hiddenGlobalKpis,
        orderBy: {
          [InventoryForecastDescriptiveField.ON_HAND_WEEKS_OF_COVER]: {
            ascending: true,
            sortPriority: 0
          }
        }
      },
      [InventoryDimension.HISTORICAL]: {
        dimensionPrettyName: 'Historical Metrics',
        dimensionSuffix: R.isNil(primaryDateRange)
          ? undefined
          : `| ${dateToMddyy(primaryDateRange.start)} - ${dateToMddyy(primaryDateRange.end)}`,
        descriptiveFields: [
          InventoryHistoricalDescriptiveField.ASIN,
          InventoryHistoricalDescriptiveField.PRODUCT_NAME,
          InventoryHistoricalDescriptiveField.KPI
        ],
        descriptiveFieldToPrettyName: INVENTORY_HISTORICAL_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
        descriptiveFieldToFormatter: INVENTORY_HISTORICAL_DESCRIPTIVE_FIELD_TO_FORMATTER,
        metricsEnum: historicalMetricsEnum,
        metricToPrettyName: historicalMetricsToPrettyName,
        rowGroupingField: InventoryHistoricalDescriptiveField.ASIN,
        rowFormatter: kpiFormatter,
        toggles: { kpis: HISTORICAL_KPI_TOGGLES, onKpiToggle: onHistoricalKpiToggle },
        hiddenGlobalKpis,
        orderBy: {
          [InventoryHistoricalDescriptiveField.ASIN]: { ascending: true, sortPriority: 0 },
          [InventoryHistoricalDescriptiveField.PRODUCT_NAME]: {
            ascending: true,
            sortPriority: 1
          }
        }
      }
    }),
    [
      primaryDateRange,
      forecastDescriptiveFields,
      forecastMetricsKeys,
      forecastMetricsEnum,
      forecastMetricsToPrettyName,
      historicalMetricsEnum,
      historicalMetricsToPrettyName,
      hiddenGlobalKpis,
      onHistoricalKpiToggle
    ]
  );

  // The master filter spans every enabled table, so it offers each shared descriptive field once
  // (with its pretty name) and suggests values from all of their rows.
  const masterFilterOptions = useMemo(
    () =>
      R.uniqBy(
        option => option.name,
        INVENTORY_DIMENSIONS.flatMap(dim =>
          inventoryDimensionConfigMap[dim].descriptiveFields.map(field => ({
            name: field,
            label: inventoryDimensionConfigMap[dim].descriptiveFieldToPrettyName[field]
          }))
        )
      ),
    [inventoryDimensionConfigMap]
  );

  const masterFilterLines = useMemo(
    () => INVENTORY_DIMENSIONS.flatMap((dim): object[] => masterDataMap[dim]),
    [masterDataMap]
  );

  const downloadMasterCsv = useCallback(
    () =>
      downloadTable(
        'inventory',
        Object.fromEntries(INVENTORY_DIMENSIONS.map(dim => [dim, masterDataMap[dim]]))
      ),
    [masterDataMap, downloadTable]
  );

  return (
    config && (
      <>
        <div className='commerce-ops-section'>
          {config.sparkChartDefaults.map(field => (
            <SparkChart
              data={primaryTimeSeries}
              otherData={compTimeSeries}
              defaultMetric={field}
              metricOptions={config.sparkChartOptions}
              columnMetaDataMap={INVENTORY_TOPLINE_METADATA_MAP}
              formatter={constructMetricFormatter(INVENTORY_METRIC_TO_FORMATTER, VOLUME_FORMATTER)}
            />
          ))}
        </div>
        <WidgetContainer
          rightActions={
            <div className='commerce-ops-header-controls'>
              {GLOBAL_KPI_TOGGLES.map(kpi => (
                <ToggleSwitch
                  key={kpi.field}
                  label={kpi.label}
                  checked={!hiddenGlobalKpis.has(kpi.field)}
                  onChange={() =>
                    onKpiToggle({
                      kpi,
                      tableLabel: 'Forecast and Historical Metrics tables',
                      hiddenKpis: hiddenGlobalKpis,
                      setHiddenKpis: setHiddenGlobalKpis,
                      setShowToast,
                      setToastLabel
                    })
                  }
                />
              ))}
              <DownloadDropdown
                size={Size.LG}
                menuOptions={XLSX_MENU_OPTIONS}
                onClickOptions={[downloadMasterCsv]}
              />
            </div>
          }
          header={
            <>
              Product Status and Inventory
              <span className='dimension-suffix'>| Forecasted and Historical</span>
            </>
          }
        >
          <div className='commerce-ops-table-stack'>
            <FilterBar
              size={Size.LG}
              onFilter={filter => setMasterFilterObj({ filterFn: filter })}
              lines={masterFilterLines}
              options={masterFilterOptions}
              storageKeySuffix='commerce-ops-inventory-master'
            />
            {INVENTORY_DIMENSIONS.map(dim => (
              <CommerceOpsTable
                topRowConfig={{ showTopRow: true, showFilterBar: false, dropdownEnabled: true }}
                dimensionConfigMap={inventoryDimensionConfigMap}
                initialDimension={dim}
                dataMap={masterDataMap}
                onDownload={downloadMasterCsv}
                filterFn={masterFilterObj?.filterFn}
              />
            ))}
          </div>
        </WidgetContainer>
        {toastLabel && (
          <Toast
            className='hidden-kpi-toast'
            onClose={onToastClose}
            show={showToast}
            delay={2500}
            autohide
          >
            <Toast.Body>
              <MdInfoOutline className='toast-info-icon' />
              {toastLabel}
              <button
                type='button'
                className='toast-close-btn'
                onClick={onToastClose}
                aria-label='Close'
              >
                <MdClose />
              </button>
            </Toast.Body>
          </Toast>
        )}
      </>
    )
  );
};

export { Inventory };
